/**
 * Users selectors (PX-W6.2) — typed reads over the users slice, `createSelector`
 * for anything derived so object identities stay stable across renders.
 */

import { createSelector } from '@reduxjs/toolkit'

import type { ApiActivityEntry, ApiInvite, ApiUser, MemberRow } from './types'
import type { UsersState } from './usersSlice'

interface WithUsers {
  users: UsersState
}

export const selectUsersState = (s: WithUsers): UsersState => s.users

export const selectUsersCache = (s: WithUsers) => s.users.cache
export const selectUsersError = (s: WithUsers) => s.users.error

/** The directory rows in server order. */
export const selectAllUsers = createSelector([selectUsersState], (users): ApiUser[] =>
  users.allIds.map((id) => users.byId[id]).filter((user): user is ApiUser => user !== undefined)
)

/** One user row by slug id, or undefined. */
export const selectUserById = (s: WithUsers, id: string | null | undefined): ApiUser | undefined =>
  id === null || id === undefined ? undefined : s.users.byId[id]

export const selectUserDetailId = (s: WithUsers): string | null => s.users.detail.id

/**
 * The detail row for the id the page REQUESTED. `detail.id` is the route's
 * own `:personId`, which is either the slug (`ApiUser.id`, the directory's
 * link) or the ObjectId hex (`ApiUser.idHex`, what a project roster row
 * carries — the admin portfolio's member chip links by it). The server
 * resolves both, but rows are stored by slug, so a hex request resolves
 * through the row's own `idHex` — the value the SERVER wrote, never a
 * client-side derivation (TASK-17A2C062 fix cycle 1, F1).
 */
export const selectUserDetail = createSelector(
  [selectUsersState],
  (users): ApiUser | undefined => {
    const requested = users.detail.id
    if (requested === null) return undefined
    return users.byId[requested] ?? Object.values(users.byId).find((user) => user.idHex === requested)
  }
)
export const selectUserDetailCache = (s: WithUsers) => s.users.detail.cache
export const selectUserDetailError = (s: WithUsers) => s.users.detail.error

/**
 * One shared empty list: a fresh `[]` per call is a new identity on every
 * render, which react-redux warns about and re-renders on
 * (`px-selector-returning-fresh-empty-array-warns`).
 */
const NO_ACTIVITY: ApiActivityEntry[] = []

export const selectUserActivity = (s: WithUsers, userId: string | null | undefined): ApiActivityEntry[] =>
  userId === null || userId === undefined ? NO_ACTIVITY : (s.users.activity.byUserId[userId] ?? NO_ACTIVITY)

export const selectInvitesState = (s: WithUsers) => s.users.invites
export const selectInviteList = (s: WithUsers) => s.users.invites.items
export const selectInvitesCache = (s: WithUsers) => s.users.invites.cache
export const selectInvitesError = (s: WithUsers) => s.users.invites.error

export const selectMembersState = (s: WithUsers) => s.users.members
export const selectMembersItems = (s: WithUsers): MemberRow[] => s.users.members.items
export const selectMembersError = (s: WithUsers) => s.users.members.error
export const selectMembersCache = (s: WithUsers) => s.users.members.cache

export const selectProjectsForSelector = (s: WithUsers) => s.users.projects.items
export const selectProjectsError = (s: WithUsers) => s.users.projects.error
export const selectProjectsCache = (s: WithUsers) => s.users.projects.cache

/**
 * The session user's canonical ObjectId hex, resolved from the directory
 * (`ApiUser.idHex`). Message rows and project members stamp their author /
 * member identity in hex while the session carries the slug — this is the one
 * bridge, and it renders what the SERVER wrote (no client-side derivation).
 * `null` until the directory has loaded the session user's row.
 */
export const selectSessionUserIdHex = createSelector(
  [selectAllUsers, (_s: WithUsers, sessionUserId: string | null) => sessionUserId],
  (users, sessionUserId): string | null => {
    if (sessionUserId === null) return null
    return users.find((user) => user.id === sessionUserId)?.idHex ?? null
  }
)

/* ── Project invites (TASK-7F3EE34E) ─────────────────────────────────────── */

/** One shared empty list (a fresh `[]` per call re-renders every reader). */
const NO_PROJECT_INVITES: ApiInvite[] = []

const selectProjectInvitesState = (s: WithUsers) => s.users.projectInvites
const selectProjectIdArg = (_s: WithUsers, projectId: string) => projectId

/** The pending invites for `projectId`; empty while the slice holds another project's list. */
export const selectProjectInviteItems = createSelector(
  [selectProjectInvitesState, selectProjectIdArg],
  (state, projectId): ApiInvite[] => (state.projectId === projectId ? state.items : NO_PROJECT_INVITES)
)

export const selectProjectInvitesCache = (s: WithUsers) => s.users.projectInvites.cache

/** The list-level read failure for `projectId` (never a row refusal, never a 403/404). */
export const selectProjectInvitesError = (s: WithUsers, projectId: string) =>
  s.users.projectInvites.projectId === projectId ? s.users.projectInvites.error : null

/** A 403/404 on the list read — rendered as the forbidden state. */
export const selectProjectInvitesBlocked = (s: WithUsers, projectId: string) =>
  s.users.projectInvites.projectId === projectId ? s.users.projectInvites.blocked : null

export const selectProjectInviteRowErrors = (s: WithUsers) => s.users.projectInvites.rowErrors
